import React, { useState } from 'react';
import './Sidebar.css';
import LogoutButton from '../Auth/LogoutButton';
import { useRugbyTracker } from '../../context/RugbyTrackerContext';
import { useTheme } from '../../hooks/useTheme';

const Sidebar = ({
    isOpen,
    toggleSidebar,
    activePage,
    setActivePage,
    onCollapsedChange
}) => {
    const { state } = useRugbyTracker();
    const { theme } = useTheme();
    const { homeTeamName, awayTeamName, homeTeamScore, awayTeamScore } = state;
    const [isCollapsed, setIsCollapsed] = useState(true); // Set to true by default

    const toggleCollapse = (e) => {
        e.stopPropagation();
        const newCollapsedState = !isCollapsed;
        setIsCollapsed(newCollapsedState);

        // Notify parent component about collapse state change
        if (onCollapsedChange) {
            onCollapsedChange(newCollapsedState);
        }
    };

    // Define menu items including settings
    const menuItems = [
        { id: 'tracking', label: 'Tracking', icon: 'field' },
        { id: 'analysis', label: 'Analysis', icon: 'chart' },
        { id: 'settings', label: 'Settings', icon: 'settings' }
    ];

    // Determine sidebar class based on state and theme
    const sidebarClass = `sidebar ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''} ${theme === 'high-contrast' ? 'high-contrast' : ''}`;

    return (
        <>
            {/* Overlay for mobile */}
            {isOpen && !isCollapsed && (
                <div className="sidebar-overlay" onClick={toggleSidebar}></div>
            )}

            {/* Sidebar */}
            <div className={sidebarClass}>
                <div className="sidebar-header">
                    <h2 className="app-title">Rugby Tracker</h2>
                    <button className="toggle-collapse" onClick={toggleCollapse}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                    </button>
                    <button className="close-sidebar" onClick={toggleSidebar}>
                        &times;
                    </button>
                </div>

                {/* Menu items */}
                <ul className="sidebar-menu">
                    {menuItems.map(item => (
                        <li
                            key={item.id}
                            className={`menu-item ${activePage === item.id ? 'active' : ''}`}
                            onClick={() => setActivePage(item.id)}
                        >
                            <div className="menu-icon">
                                {item.icon === 'field' && (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        <line x1="3" y1="9" x2="21" y2="9"></line>
                                        <line x1="3" y1="15" x2="21" y2="15"></line>
                                        <line x1="9" y1="3" x2="9" y2="21"></line>
                                        <line x1="15" y1="3" x2="15" y2="21"></line>
                                    </svg>
                                )}
                                {item.icon === 'chart' && (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="20" x2="18" y2="10"></line>
                                        <line x1="12" y1="20" x2="12" y2="4"></line>
                                        <line x1="6" y1="20" x2="6" y2="14"></line>
                                    </svg>
                                )}
                                {item.icon === 'settings' && (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="3"></circle>
                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                                    </svg>
                                )}
                            </div>
                            <span className="menu-label">{item.label}</span>
                        </li>
                    ))}
                </ul>

                {/* Match summary */}
                {!isCollapsed && (
                    <div className="sidebar-match-summary">
                        <div className="match-summary-header">Current Match</div>
                        <div className="match-summary-score">
                            <div className="team-score">
                                <div className="team-name home-team">{homeTeamName}</div>
                                <div className="score">{homeTeamScore}</div>
                            </div>
                            <div className="score-divider">-</div>
                            <div className="team-score">
                                <div className="team-name away-team">{awayTeamName}</div>
                                <div className="score">{awayTeamScore}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* App version info */}
                <div className="sidebar-footer">
                    <span className="version">Rugby Tracker v1.0</span>
                </div>

                {/* Logout button */}
                <div className="logout-container">
                    <LogoutButton />
                </div>
            </div>
        </>
    );
};

export default Sidebar;